/**
 * Faithful document import — no AI involved. Turns extracted text (or the
 * rich HTML mammoth gives us for Word files) into the book's sections/pages
 * structure with the document's own headings, wording, and order untouched.
 */

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/* A line reads as a heading when it looks like one in a printed handbook:
   markdown hashes, "3." / "3)" numbering, or a short shouted line. */
const headingLevel = (line) => {
  const t = line.trim();
  if (!t) return 0;
  const md = t.match(/^(#{1,3})\s+\S/);
  if (md) return md[1].length;
  if (t.length <= 90 && /^\d+[.)]\s+\S/.test(t) && !/[.:;,]$/.test(t)) return 2;
  const letters = t.replace(/[^a-zA-Z]/g, "");
  if (
    t.length <= 60 &&
    letters.length >= 3 &&
    letters === letters.toUpperCase() &&
    !/[.!?]$/.test(t)
  )
    return 2;
  return 0;
};

const stripMd = (line) => line.replace(/^#{1,3}\s+/, "").trim();

/** Plain extracted text -> simple faithful HTML (headings, paragraphs, lists). */
export const textToHtml = (text) => {
  const lines = (text || "").replace(/\r/g, "").split("\n");
  const out = [];
  let para = [];
  let list = null;
  const flushPara = () => {
    if (para.length) out.push(`<p>${escapeHtml(para.join(" "))}</p>`);
    para = [];
  };
  const flushList = () => {
    if (list && list.length)
      out.push(`<ul>${list.map((li) => `<li>${escapeHtml(li)}</li>`).join("")}</ul>`);
    list = null;
  };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      return;
    }
    const bullet = line.match(/^[-*•]\s+(.*)/);
    if (bullet) {
      flushPara();
      list = list || [];
      list.push(bullet[1]);
      return;
    }
    const lvl = headingLevel(line);
    if (lvl) {
      flushPara();
      flushList();
      out.push(`<h${lvl}>${escapeHtml(stripMd(line))}</h${lvl}>`);
      return;
    }
    flushList();
    para.push(line);
  });
  flushPara();
  flushList();
  return out.join("\n");
};

/**
 * Faithful HTML -> book sections. The highest heading level present splits
 * chapters; the next level splits pages inside a chapter. A document with one
 * heading level becomes one page per heading; with none, a single page.
 */
export const htmlToSections = (html, fallbackTitle) => {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;
  const nodes = [...root.childNodes].filter(
    (n) => n.nodeType === 1 || (n.nodeType === 3 && n.textContent.trim())
  );
  const levelOf = (n) =>
    n.nodeType === 1 && /^H[1-3]$/.test(n.tagName) ? Number(n.tagName[1]) : 0;
  const levels = [...new Set(nodes.map(levelOf).filter(Boolean))].sort();
  const sectionLevel = levels[0] || null;
  const pageLevel = levels[1] || null;

  const sections = [];
  let section = null;
  let page = null;
  const ensure = () => {
    if (!section) {
      section = { title: fallbackTitle || "Document", pages: [] };
      sections.push(section);
    }
    if (!page) {
      page = { title: section.pages.length ? `Page ${section.pages.length + 1}` : section.title, html: [] };
      section.pages.push(page);
    }
  };

  nodes.forEach((n) => {
    const lvl = levelOf(n);
    const heading = n.textContent.trim();
    if (lvl && lvl === sectionLevel) {
      section = { title: heading || "Untitled", pages: [] };
      sections.push(section);
      // With a two-level document the pages arrive with the next heading;
      // with a single level, the chapter itself is the page.
      page = pageLevel ? null : { title: heading || "Untitled", html: [] };
      if (page) section.pages.push(page);
      return;
    }
    if (lvl && pageLevel && lvl >= pageLevel) {
      ensure();
      page = { title: heading || "Untitled", html: [] };
      section.pages.push(page);
      return;
    }
    ensure();
    page.html.push(n.nodeType === 1 ? n.outerHTML : `<p>${escapeHtml(n.textContent.trim())}</p>`);
  });

  const built = sections
    .map((s, si) => ({
      title: s.title,
      order: si,
      pages: s.pages
        .filter((p) => p.html.length || s.pages.length === 1)
        .map((p, pi) => ({ title: p.title, content: p.html.join("\n"), order: pi })),
    }))
    .filter((s) => s.pages.length);

  // A document's title line often parses as a heading with nothing under it —
  // drop that empty leading chapter instead of shipping a blank page.
  if (
    built.length > 1 &&
    built[0].pages.length === 1 &&
    !built[0].pages[0].content.trim()
  ) {
    built.shift();
    built.forEach((sec, i) => { sec.order = i; });
  }

  if (!built.length) {
    return [
      {
        title: fallbackTitle || "Document",
        order: 0,
        pages: [{ title: fallbackTitle || "Document", content: html, order: 0 }],
      },
    ];
  }
  return built;
};
